import { DateTime } from "luxon";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLazyViewCustomerPlantGraphsAdminQuery } from "../../../../redux/api/generation/generationAPI";
import {
  selectSiteChartFilter,
  setSiteChartFilter,
  SiteChartFilter,
} from "../../../../redux/features/site-chart-filter/site-chart-filter-slice";
import ChartLoadingOverlay from "../../../../shared/components/chart-loading-overlay/chart-loading-overlay";
import SitePieChart from "../../../../shared/components/site-pie-chart/site-pie-chart";
import IViewCustomerSolarPlantGraphRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/view-customer-solar-plant-graph-request-dto";
import IViewCustomerSolarPlantGraphResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-graph-response-dto";
import { ESolarPlantGraphType } from "../../../../shared/oversight-core/enums/solar-plant-graph-type";
import { ESolarPlantViewType } from "../../../../shared/oversight-core/enums/solar-plant-view-type";
import { IGraphRecords } from "../../../../shared/oversight-core/interfaces/graph-records";
import { IGraphTotals } from "../../../../shared/oversight-core/interfaces/graph-totals";
import { IInstance } from "../../../../shared/oversight-core/interfaces/instance";
import { IOverallPlantGraph } from "../../../../shared/oversight-core/interfaces/overall-plant-graph";
import { ISolarPlantAlerts } from "../../../../shared/oversight-core/interfaces/solar-plant-alerts";
import { IVrmSolarPlantGraph } from "../../../../shared/oversight-core/interfaces/vrm-solar-plant-graph";
import { IVrmSolarPlantView } from "../../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import LeafletMapDetails from "../../../../shared/oversight-core/shared-components/leaflet-map-details/leaflet-map-details";
import SolarInfoCard from "../../../../shared/oversight-core/shared-components/solar-info-card/solar-info-card";
import AppBannerWithIcon from "../../../../shared/oversight-core/ui-elements/app-banner-with-icon/app-banner-with-icon";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import BarChart from "../../../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import ArrowIconsButton from "../../../../shared/oversight-core/ui-elements/buttons/arrow-icons-button/arrow-icons-button";
import MaterialIcon, {
  MtIcon,
} from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import MultipleChartType from "../../../../shared/oversight-core/ui-elements/multiple-chart-type/multiple-chart-type";
import { formatDateByZoneId } from "../../../../shared/oversight-core/utils/date-utils";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import { getRelativeTimeFromTimestamp } from "../../../../shared/oversight-core/utils/time-utils";
import VrmEnergyFlow from "./energy-flow/vrm-energy-flow";
import VrmSiteDashboardFilters from "./site-dashboard-filters/vrm-site-dashboard-filters";
import styles from "./site-dashboard.module.scss";

interface IProps {
  plantId: string;
  solarPlant: IVrmSolarPlantView;
  isViewingSolarPlant: boolean;
}

const graphTypeOptions: Option[] = [
  { value: ESolarPlantGraphType.LIVE_FEED, label: "System Overview" },
  { value: ESolarPlantGraphType.CONSUMPTION, label: "Consumption" },
  { value: ESolarPlantGraphType.SOLAR_YIELD, label: "Solar" },
  { value: ESolarPlantGraphType.GENERATOR, label: "Generator" },
  {
    value: ESolarPlantGraphType.GRID,
    label: "Grid",
  },
];

const VrmSiteDashboard = (props: IProps) => {
  const { plantId, solarPlant, isViewingSolarPlant } = props;

  const dispatch = useDispatch();

  const zoneId = solarPlant?.tz.formattedValue;

  const selectChartFilter: SiteChartFilter = useSelector(selectSiteChartFilter);

  const [showSiteViewDashboardFilters, setShowSiteViewDashboardFilters] =
    useState(false);
  const [quickFilter, setQuickFilter] = useState<Option>({
    value: ESolarPlantViewType.TODAY,
    label: "Today",
  });
  const [selectedGraphType, setSelectedGraphType] = useState<Option>(
    graphTypeOptions[0],
  );
  const [overallPlantGraph, setOverallPlantGraph] =
    useState<IOverallPlantGraph>();
  const [graphTotals, setGraphTotals] = useState<IGraphTotals>();
  const [graphRecords, setGraphRecords] = useState<IGraphRecords>();
  const [isHideAlerts, setIsHideAlerts] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [
    triggerCustomerPlantGraphs,
    { isFetching: isFetchingCustomerPlantGraphs },
  ] = useLazyViewCustomerPlantGraphsAdminQuery();

  useEffect(() => {
    setStartDate(new Date(selectChartFilter.startDate));
    setEndDate(new Date(selectChartFilter.endDate));
  }, [selectChartFilter]);

  useEffect(() => {
    const filterParams: IViewCustomerSolarPlantGraphRequestDTO = {
      plantId: plantId,
      viewType: selectChartFilter.viewType
        ? selectChartFilter.viewType
        : (quickFilter.value as ESolarPlantViewType),
      graphType: selectedGraphType.value as ESolarPlantGraphType,
    };

    if (selectChartFilter.isFilterCustom) {
      filterParams.startDate = formatDateByZoneId(
        selectChartFilter.startDate,
        zoneId,
      );
    }

    if (selectChartFilter.isFilterCustom) {
      filterParams.endDate = formatDateByZoneId(
        selectChartFilter.endDate,
        zoneId,
      );
    }

    if (plantId && isViewingSolarPlant) {
      triggerCustomerPlantGraphs({ ...filterParams })
        .unwrap()
        .then((res: IViewCustomerSolarPlantGraphResponseDTO) => {
          const date = res.graphData as IVrmSolarPlantGraph;

          setOverallPlantGraph(date.overallPlantGraph);
          setGraphTotals(date.graphTotals);
          setGraphRecords(date.graphRecords);
        })
        .catch(() => {
          setOverallPlantGraph(undefined);
          setGraphTotals(undefined);
        });
    }
  }, [
    triggerCustomerPlantGraphs,
    plantId,
    selectChartFilter,
    selectedGraphType,
    quickFilter,
    zoneId,
    isViewingSolarPlant,
  ]);

  const getConsumptionGraphData = useMemo(() => {
    if (!graphRecords) return [];

    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphRecords.total_consumption
          ? graphRecords.total_consumption.map((i) => i[1])
          : [];
      case ESolarPlantGraphType.CONSUMPTION:
        return graphRecords.Gc ? graphRecords.Gc.map((i) => i[1]) : [];
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphRecords.Pg ? graphRecords.Pg.map((i) => i[1]) : [];
      default:
        return [];
    }
  }, [graphRecords, selectedGraphType]);

  const getSolarGraphData = useMemo(() => {
    if (!graphRecords) return [];

    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphRecords.total_solar_yield
          ? graphRecords.total_solar_yield.map((i) => i[1])
          : [];
      case ESolarPlantGraphType.CONSUMPTION:
        return graphRecords.Pc ? graphRecords.Pc.map((i) => i[1]) : [];
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphRecords.Pc ? graphRecords.Pc.map((i) => i[1]) : [];
      case ESolarPlantGraphType.GENERATOR:
        return graphRecords.gc ? graphRecords.gc.map((i) => i[1]) : [];
      case ESolarPlantGraphType.GRID:
        return graphRecords.Gc ? graphRecords.Gc.map((i) => i[1]) : [];
      default:
        return [];
    }
  }, [graphRecords, selectedGraphType]);

  const getBatteryGraphData = useMemo(() => {
    if (!graphRecords) return [];

    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphRecords.bs ? graphRecords.bs.map((i) => i[1]) : [];
      case ESolarPlantGraphType.CONSUMPTION:
        return graphRecords.Bc ? graphRecords.Bc.map((i) => i[1]) : [];
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphRecords.Pb ? graphRecords.Pb.map((i) => i[1]) : [];
      case ESolarPlantGraphType.GENERATOR:
        return graphRecords.gb ? graphRecords.gb.map((i) => i[1]) : [];
      case ESolarPlantGraphType.GRID:
        return graphRecords.Gb ? graphRecords.Gb.map((i) => i[1]) : [];
      default:
        return [];
    }
  }, [graphRecords, selectedGraphType]);

  const getGeneratorGraphData = useMemo(() => {
    if (!graphRecords) return [];

    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphRecords.total_genset
          ? graphRecords.total_genset.map((i) => i[1])
          : [];
      case ESolarPlantGraphType.CONSUMPTION:
        return graphRecords.gc ? graphRecords.gc.map((i) => i[1]) : [];
      default:
        return [];
    }
  }, [graphRecords, selectedGraphType]);

  const getGraphTimeStamps = useMemo(() => {
    if (!graphRecords) return [];

    const allKeys = Object.keys(graphRecords);
    if (allKeys.length === 0) return [];

    const firstValidKey = allKeys.find((key) =>
      Array.isArray(graphRecords[key]),
    );
    if (!firstValidKey) return [];

    const records = graphRecords[firstValidKey];
    return records.map((entry) => entry[0]);
  }, [graphRecords]);

  const xLabelCallback = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return () => "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    let format = "HH:mm";

    if (duration <= oneHour) format = "HH:mm:ss";
    else if (duration <= 6 * oneHour) format = "HH:mm";
    else if (duration <= oneDay) format = "HH:mm";
    else if (duration <= oneWeek) format = "dd MMM";
    else if (duration <= oneMonth) format = "dd MMM";
    else if (duration <= oneYear) format = "dd MMM";
    else format = "MMM yyyy";

    return (_value: unknown, index: number) => {
      const ts = getGraphTimeStamps[index];
      if (!ts) return "";
      return DateTime.fromMillis(ts).setZone(zoneId).toFormat(format);
    };
  }, [getGraphTimeStamps, zoneId]);

  const titleCallback = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return () => "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    let mode: "hour" | "day" | "month" | "year" = "hour";

    if (duration <= oneHour) mode = "hour";
    else if (duration <= 6 * oneHour) mode = "hour";
    else if (duration <= oneDay) mode = "hour";
    else if (duration <= oneWeek) mode = "day";
    else if (duration <= oneMonth) mode = "day";
    else if (duration <= oneYear) mode = "month";
    else mode = "year";

    return (tooltipItems: any[]) => {
      const index = tooltipItems[0]?.dataIndex;
      const ts = getGraphTimeStamps[index];
      if (!ts) return "";

      const dt = DateTime.fromMillis(ts).setZone(zoneId);

      switch (mode) {
        case "hour": {
          const start = dt.toFormat("HH:mm");
          const end = dt.plus({ minutes: 59 }).toFormat("HH:mm");
          return `${start} - ${end}`;
        }

        case "day": {
          const start = dt.startOf("day").toFormat("yyyy-MM-dd HH:mm");
          const end = dt.endOf("day").toFormat("yyyy-MM-dd HH:mm");
          return `${start} - ${end}`;
        }

        case "month": {
          const start = dt.startOf("month").toFormat("yyyy-MM-dd HH:mm");
          const end = dt.endOf("month").toFormat("yyyy-MM-dd HH:mm");
          return `${start} - ${end}`;
        }

        case "year": {
          const start = dt.startOf("year").toFormat("yyyy-MM-dd HH:mm");
          const end = dt.endOf("year").toFormat("yyyy-MM-dd HH:mm");
          return `${start} - ${end}`;
        }

        default:
          return dt.toFormat("yyyy-MM-dd HH:mm");
      }
    };
  }, [getGraphTimeStamps, zoneId]);

  const xAxisLabel = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    if (duration <= oneDay) return "Hour";
    if (duration <= oneMonth) return "Day";
    if (duration <= oneYear) return "Month";
    return "Year";
  }, [getGraphTimeStamps]);

  const getFromGridValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphTotals?.grid_history_from
          ? Number(Helper.roundTo2(Number(graphTotals?.grid_history_from)))
          : 0;
      case ESolarPlantGraphType.CONSUMPTION:
        return graphTotals?.Gc
          ? Number(Helper.roundTo2(Number(graphTotals?.Gc)))
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getToGridValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphTotals?.grid_history_to
          ? Number(Helper.roundTo2(Number(graphTotals?.grid_history_to)))
          : 0;
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphTotals?.Pg
          ? Number(Helper.roundTo2(Number(graphTotals?.Pg)))
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getBatteryValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.CONSUMPTION:
        return graphTotals?.Bc
          ? Number(Helper.roundTo2(Number(graphTotals?.Bc)))
          : 0;
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphTotals?.Pb
          ? Number(Helper.roundTo2(Number(graphTotals?.Pb)))
          : 0;
      case ESolarPlantGraphType.GENERATOR:
        return graphTotals?.gb
          ? Number(Helper.roundTo2(Number(graphTotals?.gb)))
          : 0;
      case ESolarPlantGraphType.GRID:
        return graphTotals?.Gb
          ? Number(Helper.roundTo2(Number(graphTotals?.Gb)))
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getFromSolarValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphTotals?.total_solar_yield
          ? Number(Helper.roundTo2(Number(graphTotals?.total_solar_yield)))
          : 0;
      case ESolarPlantGraphType.CONSUMPTION:
        return graphTotals?.Pc
          ? Number(Helper.roundTo2(Number(graphTotals?.Pc)))
          : 0;
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphTotals?.Pc ||
          graphTotals?.Pb ||
          graphTotals?.Pg ||
          graphTotals?.Pt
          ? Number(
              Helper.roundTo2(
                Number(
                  graphTotals.Pb +
                    graphTotals.Pc +
                    graphTotals.Pg +
                    graphTotals.Pt,
                ),
              ),
            )
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getFromGeneratorValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphTotals?.total_genset
          ? Number(Helper.roundTo2(Number(graphTotals?.total_genset)))
          : 0;
      case ESolarPlantGraphType.CONSUMPTION:
        return graphTotals?.gc
          ? Number(Helper.roundTo2(Number(graphTotals?.gc)))
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getConsumptionValue = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.LIVE_FEED:
        return graphTotals?.total_consumption
          ? Number(Helper.roundTo2(Number(graphTotals?.total_consumption)))
          : 0;
      case ESolarPlantGraphType.CONSUMPTION:
        return graphTotals?.Gc ||
          graphTotals?.Pc ||
          graphTotals?.Bc ||
          graphTotals?.gc
          ? Number(
              Helper.roundTo2(
                Number(
                  graphTotals.Gc +
                    graphTotals.Pc +
                    graphTotals.Bc +
                    graphTotals.gc,
                ),
              ),
            )
          : 0;
      case ESolarPlantGraphType.SOLAR_YIELD:
        return graphTotals?.Pc
          ? Number(Helper.roundTo2(Number(graphTotals?.Pc)))
          : 0;
      case ESolarPlantGraphType.GENERATOR:
        return graphTotals?.gc
          ? Number(Helper.roundTo2(Number(graphTotals?.gc)))
          : 0;
      case ESolarPlantGraphType.GRID:
        return graphTotals?.Gc
          ? Number(Helper.roundTo2(Number(graphTotals?.Gc)))
          : 0;
      default:
        return 0;
    }
  }, [graphTotals, selectedGraphType]);

  const getResponsiveClasses = useMemo(() => {
    switch (selectedGraphType.value) {
      case ESolarPlantGraphType.SOLAR_YIELD:
        return "row-cols-xxl-4";
      case ESolarPlantGraphType.GENERATOR:
        return "row-cols-xxl-3";
      default:
        return "row-cols-xxl-5";
    }
  }, [selectedGraphType]);

  const overallPlantDataArray = useMemo(() => {
    return overallPlantGraph
      ? Object.keys(overallPlantGraph)
          .map((key) => ({
            label: key,
            totals: overallPlantGraph[key].totals,
            percentages: overallPlantGraph[key].percentages,
          }))
          .reverse()
      : [];
  }, [overallPlantGraph]);

  const getPieChartLabel = (label: string) => {
    switch (label) {
      case "today":
        return "Last 24h";
      case "week":
        return "Last 7d";
      case "month":
        return "Last 30d";
      case "year":
        return "Last 365d";
      default:
        return "";
    }
  };

  const alerts = solarPlant?.[
    "solarPlantAlerts"
  ] as unknown as ISolarPlantAlerts[];

  const getTankColor = (value: string) => {
    switch (value) {
      case "Black water (sewage)":
        return "#4E4C48";
      case "Fresh water":
        return "#397DC4";
      default:
        return "#9CAC42";
    }
  };

  const getDateDifference = () => {
    return endDate.getTime() - startDate.getTime();
  };

  const getMaterialIcon = (description: string) => {
    if (description.includes("clear")) return "clear_day";
    if (description.includes("clouds") || description.includes("cloudy"))
      return "partly_cloudy_day";
    if (description.includes("rain")) return "rainy";
    return "cloud";
  };

  const address =
    solarPlant?.address != null ? solarPlant?.address?.toString() : "";
  const temperature =
    solarPlant?.temperature_formatted != null
      ? solarPlant?.temperature_formatted.toString()
      : "";
  const temperatureIcon =
    solarPlant?.icon != null ? solarPlant?.icon.toString() : "";
  const longitude = solarPlant?.lg.rawValue;
  const latitude = solarPlant?.lt.rawValue;

  return (
    <>
      {!isHideAlerts && alerts && alerts.length > 0 && (
        <Row className="mb-2">
          <Col>
            <AppBannerWithIcon
              content={
                <>
                  {alerts.map((alert, index) => {
                    return (
                      <Row key={index}>
                        <Col
                          className="col-auto text-white font-weight-400 font-size-12 "
                          style={{ width: "20px" }}
                        >
                          {alert.instance}
                        </Col>
                        <Col className="text-white font-weight-400 font-size-12">
                          &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          {alert.description}
                        </Col>
                      </Row>
                    );
                  })}
                </>
              }
              bannerVariant="red"
              icon="emergency_home"
              iconVariant="#FFFFFF"
              iconSize={18}
              button={
                <>
                  <MaterialIcon
                    icon="close"
                    color="#ffffff"
                    size={15}
                    onClick={() => setIsHideAlerts(true)}
                    className="cursor-pointer"
                  />
                </>
              }
            />
          </Col>
        </Row>
      )}
      {solarPlant?.serviceError && !isHideAlerts ? (
        <Row className="mb-4">
          <Col>
            <AppBannerWithIcon
              content={
                <Row>
                  <Col className="text-white font-weight-400 font-size-12">
                    API is temporarily down. Showing the last recorded data
                    until live updates resume.
                  </Col>
                </Row>
              }
              bannerVariant="red"
              icon="emergency_home"
              iconVariant="#FFFFFF"
              iconSize={18}
              button={
                <>
                  <MaterialIcon
                    icon="close"
                    color="#ffffff"
                    size={15}
                    onClick={() => setIsHideAlerts(true)}
                    className="cursor-pointer"
                  />
                </>
              }
            />
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <Row className="mt-2">
        <Col className={`${styles.energyFlow} col-12 col-xxl-9`}>
          <VrmEnergyFlow
            solarPlant={solarPlant as IVrmSolarPlantView}
            zoneId={zoneId}
          />
        </Col>
        <Col className="col-xxl-3 mt-4 mt-xxl-0">
          {address || temperature || (longitude && latitude) ? (
            <Row className="mx-0 mb-0">
              <Col className={`${styles.infoCardContainer} p-3`}>
                {address && (
                  <Row className="mb-3">
                    <Col className="text-dark font-weight-500 font-size-16">
                      {address}
                    </Col>
                  </Row>
                )}
                {temperature && (
                  <Row
                    className={`${styles.todayBackground} align-items-center mx-0 p-3`}
                  >
                    <Col>
                      <Row className="align-items-center">
                        <Col className="col-auto pe-0">
                          <MaterialIcon
                            icon={getMaterialIcon(temperatureIcon) as MtIcon}
                            color="#D08700"
                          />
                        </Col>
                        <Col className="text-dark font-weight-500 font-size-16">
                          Today
                        </Col>
                      </Row>
                    </Col>
                    <Col className="col-auto text-dark font-weight-500 font-size-24">
                      {temperature}
                    </Col>
                  </Row>
                )}
                {longitude && latitude && (
                  <Row className="mt-3">
                    <Col>
                      <LeafletMapDetails
                        locations={[
                          {
                            lat: Number(latitude),
                            lng: Number(longitude),
                            name: String(solarPlant?.plantname),
                          },
                        ]}
                        height={150}
                        hidePopup
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          ) : (
            <></>
          )}
          {solarPlant?.tscn?.instances &&
            solarPlant?.tsT?.instances &&
            Object.entries(solarPlant?.tscn?.instances ?? {}).map(
              ([key, tscnInstance]: [string, IInstance], index) => {
                const tsTInstance = (
                  solarPlant?.tsT?.instances as Record<string, IInstance>
                )?.[key];

                const tsHInstance = (
                  solarPlant?.tsH?.instances as Record<string, IInstance>
                )?.[key];

                const tsBInstance = (
                  solarPlant?.tsB?.instances as Record<string, IInstance>
                )?.[key];

                return (
                  <Row key={index} className="mx-0">
                    <Col className={`${styles.infoCardContainer} p-4`}>
                      <Row className="align-items-center mb-2">
                        <Col>
                          <Row className="align-items-center">
                            <Col className="col-auto pe-0">
                              <MaterialIcon
                                icon="device_thermostat"
                                color="#4584B8"
                              />
                            </Col>
                            <Col
                              className={`${styles.letterSpacing} text-dark font-weight-500 font-size-14`}
                            >
                              {tscnInstance?.formattedValue}
                            </Col>
                          </Row>
                        </Col>
                        <Col className="col-auto text-dark font-weight-600 font-size-16">
                          {tsTInstance?.formattedValue}
                        </Col>
                      </Row>
                      <div className={styles.temperatureCard}></div>
                      {/* <Row className="row-cols-auto justify-content-between text-light font-weight-400 font-size-12 my-2">
                        <Col>Last 24h</Col>
                        <Col>22.4&deg;C min</Col>
                        <Col>30.1&deg;C max</Col>
                      </Row> */}
                      <Row className="row-cols-auto justify-content-between text-light font-weight-400 font-size-12 my-2">
                        <Col>Humidity</Col>
                        <Col>{tsHInstance?.formattedValue}</Col>
                      </Row>
                      <Row className="row-cols-auto justify-content-between text-light font-weight-400 font-size-12">
                        <Col>Barometric pressure</Col>
                        <Col>{tsBInstance?.formattedValue}</Col>
                      </Row>
                      {tsTInstance?.timestamp && (
                        <Row className="row-cols-auto justify-content-between text-light font-weight-400 font-size-12 my-2">
                          <Col>Last Updated</Col>
                          <Col>
                            {getRelativeTimeFromTimestamp(
                              tsTInstance?.timestamp,
                            )}
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                );
              },
            )}
          {Object.entries(solarPlant?.tl?.instances ?? {})
            .sort(([keyA], [keyB]) => {
              const order = ["Black water", "Fresh water", "Fuel"];

              const tfInstances = solarPlant?.tf?.instances as Record<
                string,
                IInstance
              >;
              const valueA = tfInstances?.[keyA]?.formattedValue || "";
              const valueB = tfInstances?.[keyB]?.formattedValue || "";

              return order.indexOf(valueA) - order.indexOf(valueB);
            })
            .map(([key, tlInstance]: [string, IInstance], index) => {
              const tfInstance = (
                solarPlant?.tf?.instances as Record<string, IInstance>
              )?.[key];

              return (
                <Row
                  key={index}
                  className={`${index === 0 ? `mt-4` : `mt-1`} mx-0`}
                >
                  <Col className={`${styles.infoCardContainer} p-4`}>
                    <Row className="align-items-center">
                      <Col>
                        <Row className="align-items-center">
                          <Col className="col-auto pe-0">
                            <MaterialIcon
                              icon="oil_barrel"
                              color={getTankColor(tfInstance.formattedValue)}
                            />
                          </Col>
                          <Col
                            className={`${styles.letterSpacing} text-light font-weight-500 font-size-14`}
                          >
                            {`${tfInstance.formattedValue}${
                              tfInstance.formattedValue !== "Fresh water"
                                ? `[${key}]`
                                : ``
                            }`}
                          </Col>
                        </Row>
                      </Col>
                      <Col className="col-auto text-dark font-weight-600 font-size-16">
                        {tlInstance.formattedValue}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}
        </Col>
      </Row>
      <Row className={`${styles.siteStaticContainer} py-4 px-2 mx-0 mt-4`}>
        <Col>
          <Row className="align-items-center justify-content-between">
            <Col className="col-auto text-dark font-weight-500 font-size-20">
              Site Statistics
            </Col>
            <Col className="col-12 col-lg-9 mt-1 mt-sm-0">
              <Row className="justify-content-end">
                <Col className="col-12 col-sm-6 col-lg-6 col-xl-5">
                  <AppSelect
                    options={graphTypeOptions}
                    selectedValue={selectedGraphType}
                    onChangeOption={(selectOption) => {
                      setSelectedGraphType(selectOption);
                    }}
                  />
                </Col>
                <Col className="col-12 col-sm-6 col-lg-auto mt-2 mt-sm-0">
                  <ArrowIconsButton
                    text={
                      selectChartFilter.isFilterCustom
                        ? `${moment(
                            new Date(selectChartFilter.startDate),
                          ).format("YYYY/MM/DD HH:mm")} to ${moment(
                            new Date(selectChartFilter.endDate),
                          ).format("YYYY/MM/DD HH:mm")}`
                        : String(quickFilter.label)
                    }
                    onClick={() => setShowSiteViewDashboardFilters(true)}
                    onLeftIconClick={() => {
                      const diff = getDateDifference();
                      dispatch(
                        setSiteChartFilter({
                          startDate: new Date(
                            startDate.getTime() - diff,
                          ).toISOString(),
                          endDate: new Date(
                            endDate.getTime() - diff,
                          ).toISOString(),
                          viewType: selectChartFilter.viewType,
                          isFilterCustom: true,
                        }),
                      );
                    }}
                    onRightIconClick={() => {
                      const diff = getDateDifference();
                      dispatch(
                        setSiteChartFilter({
                          startDate: new Date(
                            startDate.getTime() + diff,
                          ).toISOString(),
                          endDate: new Date(
                            endDate.getTime() + diff,
                          ).toISOString(),
                          viewType: selectChartFilter.viewType,
                          isFilterCustom: true,
                        }),
                      );
                    }}
                    showArrowIcon={selectChartFilter.isFilterCustom}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row
            className={`${getResponsiveClasses} row-cols-1 row-cols-sm-2 row-cols-lg-3 mt-3 g-3`}
          >
            {(selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ||
              selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION) && (
              <Col>
                <SolarInfoCard
                  title="Import from Grid"
                  value={`${getFromGridValue} kWh`}
                  icon="flash_on"
                  iconColor="#3E6E8C"
                  backgroundColor="#F6F7F8"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            {(selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ||
              selectedGraphType.value === ESolarPlantGraphType.SOLAR_YIELD) && (
              <Col>
                <SolarInfoCard
                  title="Export to Grid"
                  value={`${getToGridValue} kWh`}
                  icon="arrow_outward"
                  iconColor="#75E024"
                  backgroundColor="#F7F8F6"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            {selectedGraphType.value === ESolarPlantGraphType.GENERATOR && (
              <Col>
                <SolarInfoCard
                  title="Total"
                  value={`${
                    graphTotals?.gb || graphTotals?.gc
                      ? Number(
                          Helper.roundTo2(
                            Number(graphTotals.gb + graphTotals.gc),
                          ),
                        )
                      : 0
                  } kWh`}
                  icon="functions"
                  iconColor="#914ABF"
                  backgroundColor="#F6F7F8"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            {selectedGraphType.value === ESolarPlantGraphType.GRID && (
              <>
                <Col>
                  <SolarInfoCard
                    title="Total"
                    value={`${
                      graphTotals?.Gb || graphTotals?.Gc
                        ? Number(
                            Helper.roundTo2(
                              Number(graphTotals.Gb + graphTotals.Gc),
                            ),
                          )
                        : 0
                    } kWh`}
                    icon="functions"
                    iconColor="#914ABF"
                    backgroundColor="#F6F7F8"
                    isFetching={isFetchingCustomerPlantGraphs}
                  />
                </Col>
                <Col>
                  <SolarInfoCard
                    title="Battery to Grid"
                    value={`${
                      graphTotals?.Bg
                        ? Number(Helper.roundTo2(Number(graphTotals.Bg)))
                        : 0
                    } kWh`}
                    icon="arrow_outward"
                    iconColor="#75E024"
                    backgroundColor="#F7F8F6"
                    isFetching={isFetchingCustomerPlantGraphs}
                  />
                </Col>
                <Col>
                  <SolarInfoCard
                    title="PV to Grid"
                    value={`${
                      graphTotals?.Pg
                        ? Number(Helper.roundTo2(Number(graphTotals.Pg)))
                        : 0
                    } kWh`}
                    icon="arrow_outward"
                    iconColor="#75E024"
                    backgroundColor="#F7F8F6"
                    isFetching={isFetchingCustomerPlantGraphs}
                  />
                </Col>
              </>
            )}
            {selectedGraphType.value !== ESolarPlantGraphType.LIVE_FEED && (
              <Col>
                <SolarInfoCard
                  title={
                    selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION
                      ? `From Battery`
                      : `To Battery`
                  }
                  value={`${getBatteryValue} kWh`}
                  icon="battery_charging_50"
                  iconColor="#5E45B8"
                  backgroundColor="#F7F6F8"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            {(selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ||
              selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION ||
              selectedGraphType.value === ESolarPlantGraphType.SOLAR_YIELD) && (
              <Col>
                <SolarInfoCard
                  title="From Solar"
                  value={`${getFromSolarValue} kWh`}
                  icon="clear_day"
                  iconColor="#D08700"
                  backgroundColor="#F8F8F6"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            {(selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ||
              selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION) && (
              <Col>
                <SolarInfoCard
                  title="From Generator"
                  value={`${getFromGeneratorValue} kWh`}
                  icon="charger"
                  iconColor="#1F5F7F"
                  backgroundColor="#F6F8F8"
                  isFetching={isFetchingCustomerPlantGraphs}
                />
              </Col>
            )}
            <Col>
              <SolarInfoCard
                title="Consumption"
                value={`${getConsumptionValue} kWh`}
                icon="smart_outlet"
                iconColor="#E45151"
                backgroundColor="#F8F6F8"
                isFetching={isFetchingCustomerPlantGraphs}
              />
            </Col>
          </Row>
          <div className="container-white position-relative mt-4">
            {selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ? (
              <MultipleChartType
                borderColor1="#E45151"
                borderColor2="#D08700"
                borderColor3="#A45796"
                borderColor4="#5E45B8"
                backgroundColor1="#E45151"
                backgroundColor2="#D08700"
                backgroundColor3="#A45796"
                backgroundColor4="#FFFFFF14"
                label1="Consumption"
                label2="Solar"
                label3="Generator"
                label4="Battery"
                labels={getGraphTimeStamps}
                data1={getConsumptionGraphData}
                data2={getSolarGraphData}
                data3={getGeneratorGraphData}
                data4={getBatteryGraphData}
                yAxisUnitLeftSide="kWh"
                yAxisUnitRightSide="%"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const datasetType = tooltipItem.dataset.type;
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  const unit = datasetType === "bar" ? "kWh" : "%";
                  return `${label}: ${value} ${unit}`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                isChartDataAvailable={
                  getConsumptionGraphData.length > 0 ||
                  getSolarGraphData.length > 0 ||
                  getGeneratorGraphData.length > 0 ||
                  getBatteryGraphData.length > 0
                }
              />
            ) : selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION ? (
              <BarChart
                borderColor1="#3E6E8C"
                borderColor2="#D08700"
                borderColor3="#A45796"
                borderColor4="#5E45B8"
                backgroundColor1="#3E6E8C"
                backgroundColor2="#D08700"
                backgroundColor3="#A45796"
                backgroundColor4="#5E45B8"
                dataLabel1="Grid"
                dataLabel2="Solar"
                dataLabel3="Generator"
                dataLabel4="Battery"
                labels={getGraphTimeStamps}
                data1={getConsumptionGraphData}
                data2={getSolarGraphData}
                data3={getGeneratorGraphData}
                data4={getBatteryGraphData}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasForthDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getConsumptionGraphData.length > 0 ||
                  getSolarGraphData.length > 0 ||
                  getGeneratorGraphData.length > 0 ||
                  getBatteryGraphData.length > 0
                }
              />
            ) : selectedGraphType.value === ESolarPlantGraphType.SOLAR_YIELD ? (
              <BarChart
                borderColor1="#3E6E8C"
                borderColor2="#5E45B8"
                borderColor3="#D08700"
                backgroundColor1="#3E6E8C"
                backgroundColor2="#5E45B8"
                backgroundColor3=" #D08700"
                dataLabel1="To Grid"
                dataLabel2="To Battery"
                dataLabel3="Consumption"
                labels={getGraphTimeStamps}
                data1={getConsumptionGraphData}
                data2={getBatteryGraphData}
                data3={getSolarGraphData}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasThirdDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getConsumptionGraphData.length > 0 ||
                  getSolarGraphData.length > 0 ||
                  getBatteryGraphData.length > 0
                }
              />
            ) : selectedGraphType.value === ESolarPlantGraphType.GENERATOR ? (
              <BarChart
                borderColor1="#5E45B8"
                borderColor2="#A45796"
                backgroundColor1="#5E45B8"
                backgroundColor2="#A45796"
                dataLabel1="Battery"
                dataLabel2="Consumption"
                labels={getGraphTimeStamps}
                data1={getBatteryGraphData}
                data2={getSolarGraphData}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasSecondDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getSolarGraphData.length > 0 || getBatteryGraphData.length > 0
                }
              />
            ) : (
              <BarChart
                borderColor1="#5E45B8"
                borderColor2="#3E6E8C"
                backgroundColor1="#5E45B8"
                backgroundColor2="#3E6E8C"
                dataLabel1="Battery"
                dataLabel2="Consumption"
                labels={getGraphTimeStamps}
                data1={getBatteryGraphData}
                data2={getSolarGraphData}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasSecondDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getSolarGraphData.length > 0 || getBatteryGraphData.length > 0
                }
              />
            )}
            <ChartLoadingOverlay show={isFetchingCustomerPlantGraphs} />
          </div>
          {selectedGraphType.value !== ESolarPlantGraphType.LIVE_FEED && (
            <Row className="row-cols-1 row-cols-lg-2 row-cols-xl-3 row-cols-xxl-4 mt-2 g-3">
              {overallPlantDataArray.map((overallPlantData, index) => {
                return (
                  <Col key={index}>
                    <SitePieChart
                      title={getPieChartLabel(overallPlantData.label)}
                      percentages={overallPlantData.percentages}
                      totals={overallPlantData.totals}
                      selectedGraphType={
                        selectedGraphType.value as ESolarPlantGraphType
                      }
                      isFetching={isFetchingCustomerPlantGraphs}
                    />
                  </Col>
                );
              })}
            </Row>
          )}
        </Col>
      </Row>
      <VrmSiteDashboardFilters
        show={showSiteViewDashboardFilters}
        onClose={() => setShowSiteViewDashboardFilters(false)}
        setQuickFilter={setQuickFilter}
        quickFilter={quickFilter}
      />
    </>
  );
};

export default VrmSiteDashboard;
